import angular from 'angular';
import PinPage from '@sdk/newsfeed/components/PinPage/PinPage.tsx';

import { MyNewsfeedNoPages } from '@web/newsfeed/EmptyStates/MyNewsfeedNoPages/MyNewsfeedNoPages';
import newsfeedEmptyPageStatesComponent from '@web/newsfeed/EmptyStates/NewsfeedEmptyPage/NewsfeedEmptyPageStatesAngular';
import { ArticleSearchResultsAngular } from '@web/newsfeed/ArticleSearch/ArticleSearchResultsAngular';
import { NewsfeedPageAngular } from '@web/newsfeed/Page/NewsfeedPage/NewsfeedPageAngular';

import config from '@angular/config';

import { accessLevels } from '../constants';
import orgFeatureStateGuard from '../account/orgFeatureStateGuard';
import r2a from '../../react/angularIntegration/r2a';

import newsfeedMemberGroup from './directives/newsfeedMemberGroup';
import EditPageCtrl from './EditPageCtrl';
import PageSubscribedCtrl from './PageSubscribedCtrl';
import ArticleCtrl from './ArticleCtrl';
import PagesImport from './models/Pages';
import ArticlesImport from './models/Articles';
import ChannelNotificationsImport from './models/ChannelNotifications';
import CommentsImport from './models/Comments';
import NewsfeedCollectionImport from './models/NewsfeedCollection';
import newsfeedHeaderComponent from './components/newsfeedHeaderComponent';
import newsfeedPageListComponent from './components/newsfeedPageListComponent';
import newsfeedNotificationsComponent from './components/newsfeedNotificationsComponent';
import newsfeedPageWidgetsComponent from './components/newsfeedPageWidgetsComponent';
import newsfeedDescriptionWidgetComponent from './components/newsfeedDescriptionWidgetComponent';
import newsfeedManagementRouteComponent from './components/managementRouteComponent';
import newsfeedPagesRouteComponent from './components/pagesListRouteComponent';
import newsfeedPageDetailRouteComponent from './components/pageDetailRouteComponent';
import newsfeedSearchRouteComponent from './components/newsfeedSearchRouteComponent';
import maskArticleComponent from './components/maskArticle';
import introductionUrl from './introduction.html';
import pageSubscribedUrl from './page-subscribed.html';
import articleUrl from './article.html';
import editPageUrl from './edit-page.html';
import NewsfeedReactBridge from './services/NewsfeedReactBridge';

const newsfeedModule = angular.module('sling.newsfeed', []);

newsfeedModule.config(($stateProvider) => {
  $stateProvider.state('newsfeed', {
    abstract: true,
    url: '/newsfeed',
    template: '<ui-view />',
    resolve: {
      async newsfeedCollection(PersonaCollection, NewsfeedCollection) {
        await PersonaCollection.loadData();
        return await NewsfeedCollection.loadData();
      },
    },
    onEnter(Authentication, $uibModal) {
      const { adminOnboardingCompleted, newsfeedHelpShown, onboardingComplete } =
        Authentication.user.settings;
      if (!newsfeedHelpShown && (onboardingComplete || adminOnboardingCompleted)) {
        $uibModal
          .open({
            windowClass: 'sling-tour-modal sling-animate',
            templateUrl: introductionUrl,
          })
          .result.then(null, () => {
            Authentication.user.settings.newsfeedHelpShown = true;
            Authentication.syncSettings();
          });
      }
    },
    data: {
      title: 'Newsfeed',
      access: accessLevels.user,
    },
  });

  $stateProvider.state('newsfeed.view', {
    abstract: true,
    url: '',
    component: 'newsfeedPagesRoute',
  });

  $stateProvider.state('newsfeed.management', {
    abstract: true,
    url: '',
    component: 'newsfeedManagementRoute',
  });

  $stateProvider.state('newsfeed.view.mynewsfeed', {
    url: '',
    component: 'newsfeedPageDetailRoute',
    resolve: {
      pageId() {
        return 0;
      },
    },
    data: {
      title: '{{ page.name }}',
    },
  });

  $stateProvider.state('newsfeed.view.search', {
    url: '/search/page/{pageId:[0-9]+}?query&{articleId:[0-9]+}&{searchPageId:[0-9]+}',
    reloadOnSearch: false,
    component: 'newsfeedSearchRoute',
    resolve: {
      pageId($transition$) {
        const { pageId } = $transition$.params();
        return pageId ? Number(pageId) : 0;
      },
      query: ($transition$, $state, $q) => {
        'ngInject';

        const { query } = $transition$.params();
        if (query?.length && config.ARTICLE_SEARCH_ENABLED) {
          return query;
        }

        // search should not work upon empty search strings
        $state.go('newsfeed.view.mynewsfeed');
        return $q.reject('redirect');
      },
      articleId($transition$) {
        const { articleId } = $transition$.params();
        return articleId ? Number(articleId) : null;
      },
      // for details pageId is refering to articles page id, and searchPageId determines search range for back button
      // for list pageId and searchPageId are always the same
      searchPageId($transition$) {
        const { searchPageId, pageId } = $transition$.params();
        if (!searchPageId) {
          return pageId ? Number(pageId) : 0;
        }

        return searchPageId ? Number(searchPageId) : 0;
      },
    },
    data: {
      title: '{{ page.name }}',
    },
  });

  $stateProvider.state('newsfeed.view.page', {
    url: '/page/{pageId:[0-9]+}',
    component: 'newsfeedPageDetailRoute',
    resolve: {
      pageId($transition$) {
        return $transition$.params().pageId || 0;
      },
    },
    data: {
      title: '{{ page.name }}',
    },
  });

  $stateProvider.state('newsfeed.management.editPage', {
    url: '/page/{pageId:[0-9]+}/edit',
    templateUrl: editPageUrl,
    controller: EditPageCtrl,
    controllerAs: 'ctrl',
    params: {},
    resolve: {
      page(newsfeedCollection, NewsfeedCollection, $state, $transition$, $q) {
        const result = NewsfeedCollection.allChannels.find(
          (c) => String(c.id) === $transition$.params().pageId
        );
        if (result) {
          return NewsfeedCollection.getChannelDetails(result);
        }
        $state.go('newsfeed.view.mynewsfeed');
        return $q.reject('redirect');
      },
    },
    data: {
      title: '{{ page.name }}',
    },
  });

  $stateProvider.state('newsfeed.management.createPage', {
    url: '/page/create',
    templateUrl: editPageUrl,
    controller: EditPageCtrl,
    controllerAs: 'ctrl',
    resolve: {
      page: () => ({
        open: false,
        personas: [],
      }),
    },
    data: {
      title: 'Create Page',
    },
  });

  $stateProvider.state('newsfeed.view.page.subscribed', {
    url: '/subscribed',
    views: {
      '@': {
        templateUrl: pageSubscribedUrl,
        controller: PageSubscribedCtrl,
        controllerAs: 'ctrl',
      },
    },
  });

  $stateProvider.state('newsfeed.view.page.article', {
    url: '/article/{articleId:[0-9]+}?fromSearch',
    views: {
      '@newsfeed': {
        templateUrl: articleUrl,
        controller: ArticleCtrl,
        controllerAs: 'ctrl',
      },
    },
    resolve: {
      articleId($transition$) {
        return $transition$.params().articleId;
      },
      fromSearch($transition$) {
        return $transition$.params().fromSearch;
      },
    },
  });
});

newsfeedModule.run(orgFeatureStateGuard('newsfeed'));

newsfeedModule.factory('Pages', PagesImport);
newsfeedModule.factory('Articles', ArticlesImport);
newsfeedModule.factory('ChannelNotifications', ChannelNotificationsImport);
newsfeedModule.factory('Comments', CommentsImport);

newsfeedModule.service('NewsfeedCollection', NewsfeedCollectionImport);
newsfeedModule.service('NewsfeedReactBridge', NewsfeedReactBridge);

newsfeedModule.component('newsfeedManagementRoute', newsfeedManagementRouteComponent);
newsfeedModule.component('newsfeedPagesRoute', newsfeedPagesRouteComponent);
newsfeedModule.component('newsfeedPageDetailRoute', newsfeedPageDetailRouteComponent);
newsfeedModule.component('newsfeedHeader', newsfeedHeaderComponent);
newsfeedModule.component('newsfeedPageList', newsfeedPageListComponent);
newsfeedModule.component('newsfeedSearchRoute', newsfeedSearchRouteComponent);
newsfeedModule.component('newsfeedNotifications', newsfeedNotificationsComponent);
newsfeedModule.component('newsfeedPageWidgets', newsfeedPageWidgetsComponent);
newsfeedModule.component('newsfeedDescriptionWidget', newsfeedDescriptionWidgetComponent);
newsfeedModule.component('maskArticle', maskArticleComponent);
newsfeedModule.component('rPinPage', r2a(PinPage, ['isPinned', 'pageId', '&onChange']));
newsfeedModule.component('rNewsfeedEmptyPageStates', r2a(newsfeedEmptyPageStatesComponent));
newsfeedModule.component('rMyNewsfeedNoPages', r2a(MyNewsfeedNoPages, ['canCreatePages']));
newsfeedModule.component(
  'rArticleSearchResults',
  r2a(ArticleSearchResultsAngular, [
    'navigate',
    'page',
    'query',
    'articleId',
    'searchPageId',
    'newsfeedReactBridge',
  ])
);
newsfeedModule.component(
  'rNewsfeedPage',
  r2a(NewsfeedPageAngular, ['page', 'newsfeedReactBridge'])
);
newsfeedModule.directive('newsfeedMemberGroup', newsfeedMemberGroup);

export default newsfeedModule.name;
